import Point from "@arcgis/core/geometry/Point";
import {
  Tree,
  ValidationResult,
  featureTypes,
  treeCmType,
  validationFeatureCreationContext,
  validations,
} from "../lib/types";
import Polygon from "@arcgis/core/geometry/Polygon";
import Multipoint from "@arcgis/core/geometry/Multipoint";
import Map from "@arcgis/core/Map";
import { IState, getTreesLookup } from "../context/featuresReducer";
import {
  buffer,
  doesGeometryIntersectMapLayer,
  doesGeometryOverlapGeometries,
  getAreaToPerimeterRatio,
  getIntersectingPoints,
  getLTBenchmarks,
  isPolygonInvalid,
} from "./featureUtils";
import validationMessages from "../lib/simpleValidationMessages";
import mapConfig from "../lib/mapConfig";
import {
  minimumAreaToPerimeterRatio,
  scatteredTreeOffsetBenchmarkThreshold,
} from "../lib/constants";

export const checkTreeSizeAboveThreshold = (
  treeCIR: number,
  benchmarkCm: number,
  offsetBenchmarkThreshold: number
) => {
  if (offsetBenchmarkThreshold === scatteredTreeOffsetBenchmarkThreshold) {
    return treeCIR > benchmarkCm * offsetBenchmarkThreshold;
  } else {
    return treeCIR >= benchmarkCm * offsetBenchmarkThreshold;
  }
};

const checkTreeAboveDbhThreshold = (
  tree: Tree,
  offsetBenchmarkThreshold: number
) => {
  const { cmType, cm, benchmarkCm } = tree;
  const treeCIR = cmType === treeCmType.CIR ? Number(cm) : Number(cm) * Math.PI;
  return (
    !benchmarkCm ||
    checkTreeSizeAboveThreshold(treeCIR, benchmarkCm, offsetBenchmarkThreshold)
  );
};

// Individual validations

const checkInvalidPolygon = async (
  geom: Polygon,
  featureType: featureTypes,
  context: validationFeatureCreationContext
): Promise<ValidationResult> => {
  const fail = isPolygonInvalid(geom);
  if (fail) {
    if (context === validationFeatureCreationContext.upload) {
      return {
        fail: true,
        messageContent:
          validationMessages.invalidPolygon[
            featureType === featureTypes.patch
              ? "uploadPatchMessage"
              : "uploadRevegetationMessage"
          ],
      };
    }
    return {
      fail: true,
      messageTitle:
        validationMessages.invalidPolygon.getMessageTitle(featureType),
      messageContent:
        validationMessages.invalidPolygon[
          context === validationFeatureCreationContext.draw
            ? "selfIntersectingPolygonMessage"
            : "selfIntersectingPolygonAfterEditMessage"
        ],
    };
  } else return {};
};

const checkOverlappingTypes = async (
  geom: Polygon,
  featureType: featureTypes,
  supportingData: any
): Promise<ValidationResult> => {
  const fail = doesGeometryOverlapGeometries(geom, supportingData.otherGeoms);
  if (fail) {
    return {
      fail: true,
      messageTitle: validationMessages.overlappingTypes.getMessageTitle(
        featureType,
        supportingData.otherFeatureType
      ),
      messageContent: validationMessages.overlappingTypes.content(
        featureType,
        supportingData.otherFeatureType
      ),
    };
  } else return {};
};

const checkMsaLevyArea = async (
  geom: Polygon | Point | Multipoint,
  featureType: featureTypes,
  map: Map,
  context: validationFeatureCreationContext,
  supportingData?: any
): Promise<ValidationResult> => {
  const fail = await doesGeometryIntersectMapLayer(
    geom,
    map,
    mapConfig.mapServiceConfig.msaLevyAreaLayerId
  );
  if (fail) {
    if (context === validationFeatureCreationContext.upload) {
      const message = {
        [featureTypes.patch]: "uploadPatchMessage",
        [featureTypes.tree]: "uploadTreeMessage",
        [featureTypes.revegetationArea]: "uploadRevegetationMessage",
      }[featureType];
      return {
        fail: true,
        messageContent: (validationMessages.msaLevyArea as any)[message],
      };
    }
    return {
      fail: true,
      messageTitle: validationMessages.msaLevyArea.getMessageTitle(featureType),
      messageContent: validationMessages.msaLevyArea.content(
        supportingData?.nvrToolMode
      ),
    };
  } else return {};
};

const checkMsaConservation = async (
  geom: Polygon | Point,
  featureType: featureTypes,
  map: Map
): Promise<ValidationResult> => {
  const fail = await doesGeometryIntersectMapLayer(
    geom,
    map,
    mapConfig.mapServiceConfig.msaConservationLayerId
  );
  if (fail) {
    return {
      fail: true,
      messageTitle:
        validationMessages.msaConservation.getMessageTitle(featureType),
      messageContent: validationMessages.msaConservation.content(),
    };
  } else return {};
};

const checkMsa12Precincts = async (
  geom: Polygon | Point,
  featureType: featureTypes,
  map: Map
): Promise<ValidationResult> => {
  const fail = await doesGeometryIntersectMapLayer(
    geom,
    map,
    mapConfig.mapServiceConfig.msa12PrecinctsLayerId
  );
  if (fail) {
    return {
      fail: true,
      messageTitle:
        validationMessages.msa12Precincts.getMessageTitle(featureType),
      messageContent: validationMessages.msa12Precincts.content(),
    };
  } else return {};
};

const checkApRatio = async (
  geom: Polygon,
  context: validationFeatureCreationContext,
  supportingData?: any
): Promise<ValidationResult> => {
  const apRatio = supportingData?.apRatio
    ? supportingData?.apRatio
    : await getAreaToPerimeterRatio(geom);
  const fail = apRatio < minimumAreaToPerimeterRatio;
  if (fail) {
    if (context === validationFeatureCreationContext.upload) {
      return {
        fail: true,
        messageContent: validationMessages.apRatio.getUploadMessage(),
      };
    }
    return {
      fail: true,
      messageTitle: validationMessages.apRatio.title,
      messageContent: validationMessages.apRatio.content(),
    };
  } else return {};
};

const checkTreesBelowDbhThreshold = async (
  geom: Point | Polygon,
  featureType: featureTypes,
  map: Map,
  state: IState,
  context: validationFeatureCreationContext,
  supportingData?: any
): Promise<ValidationResult> => {
  if (featureType === featureTypes.patch) {
    const newIntersectingTrees = getIntersectingPoints(
      getTreesLookup(state),
      geom as Polygon
    );
    const pass = newIntersectingTrees
      .map((x) => state.features[x])
      .every((x) => checkTreeAboveDbhThreshold(x as Tree, 1));
    if (pass) {
      return {};
    } else
      return {
        fail: true,
        messageTitle:
          validationMessages.treesBelowDbhThreshold.editedPatchTitle,
        messageContent:
          validationMessages.treesBelowDbhThreshold.editedPatchContent(),
      };
  } else if (featureType === featureTypes.tree) {
    let pass = true;
    const { tree, benchmarkData, overlappingPatches } = supportingData;
    let benchmarkDataToUse: any = benchmarkData;
    if (context === validationFeatureCreationContext.upload) {
      benchmarkDataToUse = await getLTBenchmarks(geom, map);
    }
    const offsetBenchmarkThreshold = overlappingPatches.length ? 1 : 0.75;
    if (benchmarkDataToUse) {
      const benchmarks = benchmarkDataToUse.ltBenchmarks;
      pass = benchmarks.some((x: any) => {
        const newTree = { ...tree };
        const tableConfig =
          mapConfig.mapServiceConfig.tables.evcLargeTreeBenchmark;
        newTree.benchmarkCm = Number(x[tableConfig.benchmarkField]);
        return checkTreeAboveDbhThreshold(
          newTree as Tree,
          offsetBenchmarkThreshold
        );
      });
    }
    if (pass) {
      return {};
    } else {
      if (context === validationFeatureCreationContext.upload) {
        return {
          fail: true,
          messageContent:
            validationMessages.treesBelowDbhThreshold.uploadMessage,
        };
      } else
        return {
          fail: true,
          messageTitle:
            validationMessages.treesBelowDbhThreshold.editedTreeTitle,
          messageContent:
            validationMessages.treesBelowDbhThreshold.editedTreeContent(
              !!overlappingPatches.length
            ),
        };
    }
  } else return {};
};

const checkWetlands = async (
  geom: Polygon,
  featureType: featureTypes,
  map: Map,
  context: validationFeatureCreationContext
): Promise<ValidationResult> => {
  const fail = await doesGeometryIntersectMapLayer(
    geom,
    map,
    mapConfig.mapServiceConfig.wetlandsLayerId
  );
  if (!fail) return {};
  else {
    if (context === validationFeatureCreationContext.upload) {
      return {
        fail: true,
        messageContent:
          validationMessages.wetlands[
            featureType === featureTypes.patch
              ? "uploadPatchMessage"
              : "uploadRevegetationMessage"
          ],
      };
    }
    if (featureType === featureTypes.patch) {
      return {
        fail: true,
        messageTitle: validationMessages.wetlands.patchTitle,
        messageContent: validationMessages.wetlands.patchContent(),
      };
    } else if (featureType === featureTypes.revegetationArea) {
      return {
        fail: true,
        messageTitle: validationMessages.wetlands.revegetationTitle,
        messageContent: validationMessages.wetlands.revegetationContent(),
      };
    } else return {};
  }
};

const checkNonWoodyEvc = async (
  geom: Polygon,
  map: Map,
  context: validationFeatureCreationContext
): Promise<ValidationResult> => {
  const benchmarks = await getLTBenchmarks(geom, map);
  if (benchmarks && benchmarks.ltBenchmarks) {
    const woodyField =
      mapConfig.mapServiceConfig.tables.evcLargeTreeBenchmark.woodyField;
    const fail = benchmarks.ltBenchmarks.some((x) => x[woodyField] === 0);
    if (fail) {
      if (context === validationFeatureCreationContext.upload) {
        return {
          fail: true,
          messageContent: validationMessages.nonWoodyEvc.uploadMessage,
        };
      } else
        return {
          fail: true,
          messageTitle: validationMessages.nonWoodyEvc.title,
          messageContent: validationMessages.nonWoodyEvc.content(),
        };
    }
  }
  return {};
};

const checkParcelBoundaryProximity = async (
  geom: Polygon | Point,
  featureType: featureTypes,
  map: Map,
  context: validationFeatureCreationContext
): Promise<ValidationResult> => {
  let geometry: any = geom;
  const geometryToBuffer =
    featureType === featureTypes.tree ? new Point(geom) : new Polygon(geom);
  const bufferResult = await buffer([geometryToBuffer], 6);
  geometry = bufferResult.length ? bufferResult[0] : bufferResult;
  const geomWithinParcel = await doesGeometryIntersectMapLayer(
    geometry,
    map,
    mapConfig.mapServiceConfig.msaParcelLayerId,
    "within"
  );
  const geomIntersectsParcelLayer = await doesGeometryIntersectMapLayer(
    geometry,
    map,
    mapConfig.mapServiceConfig.msaParcelLayerId
  );
  if (geomIntersectsParcelLayer && !geomWithinParcel) {
    if (context === validationFeatureCreationContext.upload) {
      return {
        fail: true,
        messageContent:
          validationMessages.parcelBoundaryProximity.uploadMessage,
      };
    } else
      return {
        fail: true,
        messageContent: validationMessages.parcelBoundaryProximity.content(),
      };
  } else return {};
};

const checkOutsideSupportingRasters = async (
  geom: Polygon | Point,
  map: Map,
  context: validationFeatureCreationContext
): Promise<ValidationResult> => {
  const geomWithinExtent = await doesGeometryIntersectMapLayer(
    geom,
    map,
    mapConfig.mapServiceConfig.nvrExtentLayerId,
    "within"
  );
  if (!geomWithinExtent) {
    if (context === validationFeatureCreationContext.upload) {
      return {
        fail: true,
        messageContent:
          validationMessages.outsideSupportingRasters.uploadMessage,
      };
    } else
      return {
        fail: true,
        messageTitle: validationMessages.outsideSupportingRasters.title,
        messageContent: validationMessages.outsideSupportingRasters.content(),
      };
  } else return {};
};

// Validation runner

export const runValidations = async (
  validationsToRun: validations[],
  geom: Point | Polygon | Multipoint,
  featureType: featureTypes,
  map: Map,
  state?: IState,
  context?: validationFeatureCreationContext,
  supportingData?: any
): Promise<ValidationResult[]> => {
  const promises: Promise<ValidationResult>[] = [];
  if (validationsToRun.includes(validations.invalidPolygon)) {
    promises.push(
      checkInvalidPolygon(
        geom as Polygon,
        featureType,
        context as validationFeatureCreationContext
      )
    );
  }
  if (validationsToRun.includes(validations.overlappingTypes)) {
    promises.push(
      checkOverlappingTypes(geom as Polygon, featureType, supportingData)
    );
  }
  if (validationsToRun.includes(validations.msaLevyArea)) {
    promises.push(
      checkMsaLevyArea(
        geom as Polygon | Point | Multipoint,
        featureType,
        map,
        context as validationFeatureCreationContext,
        supportingData
      )
    );
  }
  if (validationsToRun.includes(validations.msaConservation)) {
    promises.push(
      checkMsaConservation(geom as Polygon | Point, featureType, map)
    );
  }
  if (validationsToRun.includes(validations.msa12Precincts)) {
    promises.push(
      checkMsa12Precincts(geom as Polygon | Point, featureType, map)
    );
  }
  if (validationsToRun.includes(validations.apRatio)) {
    promises.push(
      checkApRatio(
        geom as Polygon,
        context as validationFeatureCreationContext,
        supportingData
      )
    );
  }
  if (validationsToRun.includes(validations.treesBelowDbhThreshold)) {
    promises.push(
      checkTreesBelowDbhThreshold(
        geom as Point | Polygon,
        featureType,
        map,
        state as IState,
        context as validationFeatureCreationContext,
        supportingData
      )
    );
  }
  if (validationsToRun.includes(validations.wetlands)) {
    promises.push(
      checkWetlands(
        geom as Polygon,
        featureType,
        map,
        context as validationFeatureCreationContext
      )
    );
  }
  if (validationsToRun.includes(validations.nonWoodyEvc)) {
    promises.push(
      checkNonWoodyEvc(
        geom as Polygon,
        map,
        context as validationFeatureCreationContext
      )
    );
  }
  if (validationsToRun.includes(validations.parcelBoundaryProximity)) {
    promises.push(
      checkParcelBoundaryProximity(
        geom as Polygon,
        featureType,
        map,
        context as validationFeatureCreationContext
      )
    );
  }
  if (validationsToRun.includes(validations.outsideSupportingRasters)) {
    promises.push(
      checkOutsideSupportingRasters(
        geom as Polygon,
        map,
        context as validationFeatureCreationContext
      )
    );
  }
  return await Promise.all(promises);
};
